import React from "react";
import { useRef, useEffect, useState } from "react";
import { Animated, ViewProps, View } from "react-native";

export type CollapsibleProps = ViewProps & {
  isCollapsed: boolean;
  accessibilityLabel?: string;
  accessibilityLabelledBy?: string;
  animationSpeed?: number;
};

const defaultAnimationSpeed = 300;

export const Collapsible: React.FC<CollapsibleProps> = (props) => {
  const {
    animationSpeed = defaultAnimationSpeed,
    isCollapsed,
    children,
    style,
    accessibilityRole = "region",
    accessibilityLabel,
    accessibilityLabelledBy,
    ...viewProps
  } = props;

  const [childHeight, setChildHeight] = useState<number | undefined>(undefined);
  const height = useRef<Animated.Value | undefined>(
    isCollapsed ? new Animated.Value(0) : undefined
  );
  const [visibility, setVisibility] = useState<"visible" | "hidden">(
    isCollapsed ? "hidden" : "visible"
  );

  useEffect(() => {
    height.current?.addListener(({ value }) => {
      // Putting this here instead inside `Animated.timing().start()` because we want it to change
      // as soon as it's anything bigger than 0.
      if (value > 0) {
        visibility !== "visible" && setVisibility("visible");
      }
    });
    return () => {
      height.current?.removeAllListeners();
    };
  }, [visibility]);

  // Set or update child/content height.
  useEffect(() => {
    if (childHeight === undefined) {
      return;
    }

    if (height.current === undefined) {
      height.current = new Animated.Value(childHeight);
    } else if (!isCollapsed) {
      height.current.setValue(childHeight);
    }
    // We only want this to set the height when child height is changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childHeight]);

  // Animate the collapse/expansion based on `isCollapsed` props.
  useEffect(() => {
    if (height.current === undefined || childHeight === undefined) {
      return;
    }

    if (isCollapsed) {
      Animated.timing(height.current, {
        toValue: 0,
        duration: animationSpeed,
        useNativeDriver: false,
      }).start(() => setVisibility("hidden"));
    } else {
      Animated.timing(height.current, {
        toValue: childHeight,
        duration: animationSpeed,
        useNativeDriver: false,
      }).start();
    }
    // We only want to animate when isCollapsed is changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsed]);

  return (
    <Animated.View
      {...viewProps}
      style={[
        style,
        {
          // Visibility exists for the web, but not for mobile.
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          visibility,
          height: height.current,
          overflow: "hidden",
        },
      ]}
      // @ts-expect-error TS2769: No overload matches this call.
      accessibilityRole={accessibilityRole}
      // TODO: This is a workaround for accessibilityLabelledBy not working correctly. Rely on the
      // accessibilityLabelledBy when that works and remove this.
      accessibilityLabel={accessibilityLabel}
      // TODO: accessibilityLabelledBy is not working (per TS and DOM). I'm leaving it here so the
      // consumer of this component can still provide it (building habit).
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      accessibilityLabelledBy={accessibilityLabelledBy}
      // accessibilityExpanded actually make it to the DOM okay, despite what TS says about its
      // non-existent.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      accessibilityExpanded={!isCollapsed}
    >
      <View
        testID={`${accessibilityLabel}_collapsibleContent`}
        onLayout={(event) => setChildHeight(event.nativeEvent.layout.height)}
      >
        {children}
      </View>
    </Animated.View>
  );
};

export default Collapsible;
